/**
 * Grades a sung take against the notes of one part.
 *
 * Inputs are plain arrays so this runs anywhere:
 * - notes: { start, end, midi, ref, tuning? } in score seconds (the player's
 *   baked note-event time), one per note of the part; `ref` identifies the
 *   notehead (OSMD's source note) and `tuning` ([{ from, to, cents }]) moves
 *   the target off equal temperament over time (see justIntonation.js).
 * - frames: pitch-tracker output, { f0, clarity, rms }, one per hop.
 * - positions: for each frame, where playback was at that instant:
 *   { pass, time } (time in score seconds), or null when not playing. A new
 *   pass starts each time a looping section comes round again.
 *
 * What is sung at score time t reaches the mic some time later (output
 * latency, input latency). That lag is estimated from the take itself:
 * frame i is placed at the score position of frame i - lagFrames.
 *
 * Grading is note-first. The singing is first lined up with the written
 * notes in order, allowing each note to come in a little early or late,
 * after a consonant or a breath (see align); each note is then graded on
 * what was sung for it, from where its pitch settled.
 */

// Pitch clarity (1 - YIN aperiodicity): a clear vowel is well above 0.8,
// breath and "s" are low. Frames between WEAK and MIN count only when they
// carry on a neighbour's pitch line.
const CLEAR_CLARITY = 0.75;
// ...and grading uses only those when they make up this share of a note
const CLEAR_SHARE = 0.4;
const MIN_CLARITY = 0.6;
const WEAK_CLARITY = 0.45;
const CONTINUITY_SEMITONES = 0.5;
const MIN_RUN_SECONDS = 0.05;
const MIN_HZ = 60;
const MAX_HZ = 1200;
// A sung frame must reach this share of the take's loud level
const LEVEL_SHARE = 0.05;
const ABSOLUTE_LEVEL = 0.0005;
// ...and stand ROOM_MARGIN times above the room's own level (its quietest
// QUIET_WINDOW_SECONDS), though never asked for more than MAX_ROOM_SHARE of
// the loud level
const QUIET_WINDOW_SECONDS = 0.5;
const ROOM_MARGIN = 2.5;
const MAX_ROOM_SHARE = 0.2;
export const IN_TUNE_CENTS = 25;
export const CLOSE_CENTS = 50;

// Lining the singing up with the notes, note by note. Costs are per 10 ms
// frame, in semitones of mismatch.
// - A frame two semitones or more from a note is plainly not that note.
const MISMATCH_CAP = 2;
// - Unpitched sound (a consonant, a breath) where a note is written, and
//   singing where a rest is written.
const UNPITCHED_IN_NOTE = 0.5;
const PITCHED_IN_REST = 1;
// - Time: singers come in late far more often than early. Within the slack
//   a frame drifts from its written span cheaply; beyond it, steeply.
const EARLY_SLACK = 0.12;
const LATE_SLACK = 0.3;
const DRIFT_COST = 1.5; // per second outside the written span
const STEEP_COST = 20; // per second beyond the slack
// - A jump out of a dead end (should not happen; it keeps the path whole)
const JUMP_COST = 5;
// Frames only consider notes within this reach of their time
const REACH_EARLY = 0.4;
const REACH_LATE = 0.7;
// Grading a note's frames: settled once a short median of the sung pitch
// comes within reach of the next SETTLE_LOOKAHEAD_SECONDS (reach: 1.5 x the
// vibrato's spread, between these bounds); the last frames (the move to the
// next note) are left out; a note needs this much pitched singing to be
// graded at all.
const SETTLED_SEMITONES = 0.8;
const MIN_SETTLED_SEMITONES = 0.12;
const SETTLE_LOOKAHEAD_SECONDS = 0.1;
// A note held still this close to its held pitch counts as settled (a
// held note whose just target moves keeps both its levels within this)
const NEAR_HELD_SEMITONES = 0.4;
const RELEASE_FRAMES = 3;
const MIN_GRADED_FRAMES = 4;
// Readings this close (after averaging out vibrato) count as one held pitch
const HELD_BAND = 0.2;
const MIN_SUNG_SECONDS = 0.04;
// Notes shorter than this are ornaments: not graded
const SHORTEST_GRADED = 0.06;
// A scoop: arriving at least this far from the pitch, taking at least this
// long to get there; a legato glide on from the previous note (singing
// within LEGATO_GAP before) counts only when it takes SLIDE_SECONDS
const SCOOP_CENTS = 40;
const SCOOP_SECONDS = 0.08;
const SLIDE_SECONDS = 0.15;
const LEGATO_GAP = 0.06;
// On the way into a note, a step bigger than this between neighbouring
// readings is a jump (a burst at the start of a word), not a scoop
const JUMP_IN_SEMITONES = 0.6;
// refineLag: how many sung note changes it needs, and how many rounds
const MIN_MOVES = 6;
const REFINE_ROUNDS = 3;
// Lag search range, seconds
const LAG_MIN = -0.05;
const LAG_MAX = 0.45;
// Matched-frame weight needed before trusting a lag estimate
const MIN_LAG_EVIDENCE = 40;
// Pitch moving faster than this is a scoop or a slide, not a held note.
// Vibrato of +-50 cents at 6 Hz peaks near 19 semitones a second.
const MAX_STEADY_SEMITONES_PER_SECOND = 15;

export const hzToMidi = (hz) => 69 + 12 * Math.log2(hz / 440);

/** 'good' within IN_TUNE_CENTS, 'close' within CLOSE_CENTS, else 'off'. */
export const gradeOf = (cents) => {
  const size = Math.abs(cents);
  return size <= IN_TUNE_CENTS ? 'good' : size <= CLOSE_CENTS ? 'close' : 'off';
};

const NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
export const pitchClassName = (midi) => NAMES[((Math.round(midi) % 12) + 12) % 12];

// Fold an interval in semitones into [-6, 6): octave errors are forgiven
const foldOctave = (semitones) => semitones - 12 * Math.round(semitones / 12);

// A note's target pitch at a score time: its equal-tempered MIDI number
// moved by the cents of the tuning stretch in force then (or its `cents`)
const tunedAt = (note, time) => {
  if (note.tuning && note.tuning.length) {
    const stretch = note.tuning.find(t => time < t.to) || note.tuning[note.tuning.length - 1];
    return note.midi + stretch.cents / 100;
  }
  return note.midi + (note.cents || 0) / 100;
};

// Mean of the middle half: as robust as a median to a scoop, a breath or a
// stray octave reading, but it weighs the whole note rather than whichever
// stretch of it was longest (vibrato averages out either way)
const interquartileMean = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const cut = Math.floor(sorted.length / 4);
  const middle = sorted.slice(cut, sorted.length - cut);
  return middle.reduce((sum, v) => sum + v, 0) / middle.length;
};

// The centre of a note's readings, one per hop in time order. Vibrato
// swings evenly either side of the pitch meant, but a window holding part
// of a cycle leans toward the half it caught (a few cents for a wide
// vibrato). When the readings repeat at a vibrato rate (4-8 Hz, found by
// autocorrelation), smooth them over exactly one period first, which
// cancels the swing wherever it starts or stops.
const VIBRATO_HZ = [4, 8];
// The vibrato's period in hops, or 0 when the readings do not repeat at a
// vibrato rate. Judged on the step from each reading to the next, so a
// one-way drift such as a scoop does not drown the swing out.
const vibratoPeriod = (values, hopSeconds) => {
  const steps = values.slice(1).map((v, i) => v - values[i]);
  const n = steps.length;
  const shortest = Math.max(2, Math.round(1 / (VIBRATO_HZ[1] * hopSeconds)));
  // Each candidate period needs a few overlapping steps to compare
  const longest = Math.min(Math.round(1 / (VIBRATO_HZ[0] * hopSeconds)), n - 6);
  if (longest < shortest) return 0;
  const mean = steps.reduce((sum, v) => sum + v, 0) / n;
  const centred = steps.map(v => v - mean);
  const energy = centred.reduce((sum, v) => sum + v * v, 0);
  if (energy === 0) return 0;
  let period = 0;
  let strongest = 0.3; // weaker repetition than this is not vibrato
  for (let lag = shortest; lag <= longest; lag++) {
    let sum = 0;
    for (let i = lag; i < n; i++) sum += centred[i] * centred[i - lag];
    const correlation = (sum / (n - lag)) / (energy / n);
    if (correlation > strongest) {
      strongest = correlation;
      period = lag;
    }
  }
  return period;
};

// The pitch a note was held at: with any vibrato averaged out (a centred
// mean over one period), the largest cluster of readings within
// HELD_BAND of each other, later ones winning ties (a scoop comes first).
// Returns that level and the spread of the raw readings in the cluster
// (the vibrato's width, or a straight tone's wobble).
const heldPitch = (line, hopSeconds) => {
  const period = vibratoPeriod(line, hopSeconds);
  const half = period >> 1;
  const smooth = period
    ? line.map((v, i) => {
      const from = Math.max(0, i - half);
      const to = Math.min(line.length, i + half + 1);
      let sum = 0;
      for (let k = from; k < to; k++) sum += line[k];
      return sum / (to - from);
    })
    : line;
  let best = smooth[0];
  let most = -1;
  for (const candidate of smooth) {
    let count = 0;
    for (const v of smooth) if (Math.abs(v - candidate) <= HELD_BAND) count++;
    if (count >= most) {
      most = count;
      best = candidate;
    }
  }
  const members = [];
  smooth.forEach((v, i) => { if (Math.abs(v - best) <= HELD_BAND) members.push(i); });
  const level = members.reduce((sum, i) => sum + smooth[i], 0) / members.length;
  const swing = Math.sqrt(members.reduce((sum, i) => sum + (line[i] - level) ** 2, 0) / members.length);
  return { level, swing };
};

const centreOf = (values, hopSeconds) => {
  const period = vibratoPeriod(values, hopSeconds);
  if (!period) return interquartileMean(values);
  const smoothed = [];
  let running = 0;
  for (let i = 0; i < values.length; i++) {
    running += values[i];
    if (i >= period) running -= values[i - period];
    if (i >= period - 1) smoothed.push(running / period);
  }
  return interquartileMean(smoothed);
};

/**
 * One slot per onset: notes of the part that start together (a chord or
 * divisi on one staff) form one slot, and the singer is graded against
 * whichever of them they sang.
 */
export function buildSlots(notes) {
  const sorted = [...notes].sort((a, b) => a.start - b.start);
  const slots = [];
  for (const note of sorted) {
    const last = slots[slots.length - 1];
    if (last && Math.abs(last.start - note.start) < 0.001) {
      last.notes.push(note);
      last.end = Math.min(last.end, note.end);
    } else {
      slots.push({ start: note.start, end: note.end, notes: [note] });
    }
  }
  return slots;
}

// Index of the last slot starting at or before time (slots sorted by start)
function slotAt(slots, time) {
  let low = 0;
  let high = slots.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (slots[mid].start <= time) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}

/**
 * Which frames hold singing: pitched, in range and loud enough. A less
 * clearly pitched frame (a breathy tone, a voiced consonant) still counts
 * when it carries on the pitch line of a neighbour that does. Pitched runs
 * shorter than MIN_RUN_SECONDS (a click, a knock, a cough) do not.
 */
export function sungFrames(frames, hopSeconds = 0.01) {
  const levels = frames.filter(f => f.clarity >= CLEAR_CLARITY).map(f => f.rms).sort((a, b) => a - b);
  const loud = levels.length ? levels[Math.floor(levels.length * 0.95)] : 0;
  // The room's own level: the quietest half second (median level). A hum
  // or a fan can be steady enough to read as a pitch, so singing must
  // stand clear of it; capped so soft singing still counts in a take with
  // no pauses at all.
  const window = Math.max(5, Math.round(QUIET_WINDOW_SECONDS / hopSeconds));
  let room = Infinity;
  for (let i = 0; i + window <= frames.length; i += Math.ceil(window / 2)) {
    const slice = frames.slice(i, i + window).map(f => f.rms).sort((a, b) => a - b);
    room = Math.min(room, slice[slice.length >> 1]);
  }
  const aboveRoom = Number.isFinite(room) ? Math.min(room * ROOM_MARGIN, loud * MAX_ROOM_SHARE) : 0;
  const gate = Math.max(ABSOLUTE_LEVEL, loud * LEVEL_SHARE, aboveRoom);
  const usable = (f) => f.f0 >= MIN_HZ && f.f0 <= MAX_HZ && f.rms >= gate;
  const midis = frames.map(f => hzToMidi(f.f0));
  const sung = frames.map(f => usable(f) && f.clarity >= MIN_CLARITY);
  const carriesOn = (i, j) => (
    !sung[i] && sung[j] && usable(frames[i]) && frames[i].clarity >= WEAK_CLARITY
    && Math.abs(midis[i] - midis[j]) <= CONTINUITY_SEMITONES
  );
  for (let i = 1; i < frames.length; i++) if (carriesOn(i, i - 1)) sung[i] = true;
  for (let i = frames.length - 2; i >= 0; i--) if (carriesOn(i, i + 1)) sung[i] = true;
  const shortest = Math.round(MIN_RUN_SECONDS / hopSeconds);
  for (let i = 0; i < sung.length;) {
    if (!sung[i]) {
      i++;
      continue;
    }
    let end = i;
    while (end < sung.length && sung[end]) end++;
    if (end - i < shortest) sung.fill(false, i, end);
    i = end;
  }
  return sung;
}

/**
 * The lag, in frames, that best lines the sung pitches up with the notes.
 * Each sung frame scores by how close it is (octave-folded) to a note
 * sounding at its lagged position, and loses a little for landing in a rest.
 * Only frames where the pitch is holding still count: a scoop into a note
 * or a slide between notes would otherwise pull the lag late. Vibrato moves
 * slowly enough to count. Falls back to `priorFrames` until there is enough
 * singing to judge.
 */
export function estimateLag({ frames, positions, slots, sung, hopSeconds, priorFrames }) {
  const minLag = Math.round(LAG_MIN / hopSeconds);
  const maxLag = Math.round(LAG_MAX / hopSeconds);
  const midis = frames.map(f => hzToMidi(f.f0));
  const steadyStep = MAX_STEADY_SEMITONES_PER_SECOND * hopSeconds;
  const steady = midis.map((m, i) => (
    sung[i] && i > 0 && i < midis.length - 1 && sung[i - 1] && sung[i + 1]
    && Math.abs(midis[i + 1] - midis[i - 1]) <= 2 * steadyStep
  ));
  const scores = [];
  for (let lag = minLag; lag <= maxLag; lag++) {
    let score = 0;
    for (let i = Math.max(0, lag); i < frames.length; i++) {
      if (!steady[i]) continue;
      const position = positions[i - lag];
      if (!position) continue;
      const index = slotAt(slots, position.time);
      const slot = index >= 0 ? slots[index] : null;
      if (!slot || position.time >= slot.end) {
        score -= 0.2;
        continue;
      }
      let closest = Infinity;
      for (const note of slot.notes) {
        closest = Math.min(closest, Math.abs(foldOctave(midis[i] - note.midi)));
      }
      score += Math.max(0, 1 - closest);
    }
    scores.push({ lag, score });
  }
  // A held note fits equally well over a range of lags (its steady middle
  // stays inside the note); the edges of the range are where note changes
  // stop lining up. Take the middle of the best range, which centres each
  // sung note on its written one.
  const top = Math.max(...scores.map(s => s.score));
  if (!(top >= MIN_LAG_EVIDENCE)) return priorFrames;
  const plateau = scores.filter(s => s.score >= top - Math.max(1, top * 0.01)).map(s => s.lag);
  return plateau[plateau.length >> 1];
}

// The cost of hearing `point` ({ time, midi | null }) as part of `state`
// (a note's slot, or a rest), and of it being there in time
function frameCost(point, state) {
  let cost;
  if (state.slot) {
    if (point.midi === null) {
      cost = UNPITCHED_IN_NOTE;
    } else {
      cost = MISMATCH_CAP;
      for (const note of state.slot.notes) {
        cost = Math.min(cost, Math.abs(foldOctave(point.midi - tunedAt(note, point.time))));
      }
    }
  } else {
    cost = point.midi === null ? 0 : PITCHED_IN_REST;
  }
  const early = state.from - point.time;
  const late = point.time - state.to;
  if (early > 0) cost += DRIFT_COST * early + (early > EARLY_SLACK ? STEEP_COST * (early - EARLY_SLACK) : 0);
  else if (late > 0) cost += DRIFT_COST * late + (late > LATE_SLACK ? STEEP_COST * (late - LATE_SLACK) : 0);
  return cost;
}

// The written sequence as states: a rest before, between and after the
// notes, so silence and breaths have somewhere to go
function statesOf(slots) {
  const states = [{ from: -Infinity, to: slots.length ? slots[0].start : Infinity }];
  let reached = -Infinity;
  slots.forEach((slot, index) => {
    if (index > 0 && slot.start - reached >= 0.05) states.push({ from: reached, to: slot.start });
    states.push({ from: slot.start, to: slot.end, slot, index });
    reached = Math.max(reached, slot.end);
  });
  states.push({ from: reached, to: Infinity });
  return states;
}

/**
 * Which state each point belongs to: the cheapest path that moves through
 * the written sequence in order (a frame stays with the current note or
 * moves on to the next), so a late entry is heard as the previous note
 * held on, and a consonant before a vowel as not yet the note.
 */
function align(points, states) {
  const count = states.length;
  let previous = new Float64Array(count).fill(Infinity);
  let current = new Float64Array(count).fill(Infinity);
  const steps = []; // per point: { low, from } with from[s - low] the state it came from
  let low = 0;
  let high = -1;
  let previousLow = 0;
  let previousHigh = -1;
  points.forEach((point, f) => {
    while (low < count - 1 && states[low].to + REACH_LATE < point.time) low++;
    while (high < count - 1 && states[high + 1].from - REACH_EARLY <= point.time) high++;
    if (high < low) high = low;
    // The cheapest way in from the previous frame, for a dead-end jump
    let cheapest = Infinity;
    let cheapestState = -1;
    for (let s = previousLow; s <= previousHigh; s++) {
      if (previous[s] < cheapest) {
        cheapest = previous[s];
        cheapestState = s;
      }
    }
    const from = new Int32Array(high - low + 1);
    for (let s = low; s <= high; s++) {
      let best;
      let origin;
      if (f === 0) {
        best = 0;
        origin = -1;
      } else {
        const stay = previous[s];
        const advance = s > 0 ? previous[s - 1] : Infinity;
        best = Math.min(stay, advance);
        origin = stay <= advance ? s : s - 1;
        if (cheapest + JUMP_COST < best) {
          best = cheapest + JUMP_COST;
          origin = cheapestState;
        }
      }
      current[s] = best + frameCost(point, states[s]);
      from[s - low] = origin;
    }
    steps.push({ low, from });
    for (let s = previousLow; s <= previousHigh; s++) previous[s] = Infinity;
    [previous, current] = [current, previous];
    previousLow = low;
    previousHigh = high;
  });

  // End wherever is cheapest (a take can stop mid-song), then walk back
  const assigned = new Int32Array(points.length);
  let state = previousLow;
  for (let s = previousLow; s <= previousHigh; s++) {
    if (previous[s] < previous[state]) state = s;
  }
  for (let f = points.length - 1; f >= 0; f--) {
    assigned[f] = state;
    const { low: stepLow, from } = steps[f];
    state = from[state - stepLow];
  }
  return assigned;
}

// Each pass's frames in score time, pitched (midi) or not (null), with the
// input sample each frame was measured at
function pointsByPass(frames, positions, sung, lagFrames) {
  const passes = new Map();
  let lastPass = -1;
  for (let i = Math.max(0, lagFrames); i < frames.length; i++) {
    const position = positions[i - lagFrames];
    if (!position) continue;
    if (!passes.has(position.pass)) passes.set(position.pass, []);
    passes.get(position.pass).push({ time: position.time, midi: sung[i] ? hzToMidi(frames[i].f0) : null, clarity: frames[i].clarity, sample: frames[i].sample });
    lastPass = Math.max(lastPass, position.pass);
  }
  return { passes, lastPass };
}

/**
 * Corrects a lag estimate on the moments the singer moved from one note to
 * the next. estimateLag lines held pitches up with held notes, which leaves
 * the lag loose by however long the glides between notes take. Here the
 * singing is lined up note by note (see align) and the median gap between
 * each sung move and the written note change it belongs to is the lag left
 * over; only moves straight from one sung note to another at least a
 * semitone away count, as those are sharp. Repeated until it settles.
 */
export function refineLag({ frames, positions, slots, sung, lagFrames, hopSeconds = 0.01 }) {
  const states = statesOf(slots);
  let lag = lagFrames;
  for (let round = 0; round < REFINE_ROUNDS; round++) {
    const { passes } = pointsByPass(frames, positions, sung, lag);
    const gaps = [];
    let scoreSeconds = 0;
    let count = 0;
    passes.forEach((points) => {
      if (points.length < 2) return;
      // Score seconds per real second (playback can be slowed or sped up)
      scoreSeconds += points[points.length - 1].time - points[0].time;
      count += points.length - 1;
      const assigned = align(points, states);
      for (let f = 1; f < points.length; f++) {
        if (assigned[f] !== assigned[f - 1] + 1) continue;
        const before = states[assigned[f - 1]];
        const after = states[assigned[f]];
        if (!before.slot || !after.slot || points[f - 1].midi === null || points[f].midi === null) continue;
        if (Math.abs(before.slot.notes[0].midi - after.slot.notes[0].midi) < 1) continue;
        gaps.push(points[f].time - after.slot.start);
      }
    });
    if (gaps.length < MIN_MOVES || count === 0) break;
    gaps.sort((a, b) => a - b);
    const rate = scoreSeconds / (count * hopSeconds) || 1;
    const shift = Math.round(gaps[gaps.length >> 1] / rate / hopSeconds);
    if (Math.abs(shift) < 1) break;
    lag = Math.max(Math.round(LAG_MIN / hopSeconds), Math.min(Math.round(LAG_MAX / hopSeconds), lag + shift));
  }
  return lag;
}

/**
 * Judges one stretch of singing against the note(s) it was meant to be:
 * where the voice settled (a scoop in costs nothing), where it started to
 * leave (the glide on is left out), the pitch in between against the
 * nearest candidate note (octaves forgiven, each frame against the target
 * of its moment), and any scoop. Shared by the grader and the pitch
 * monitor, so both judge the same way.
 * heard: pitched readings [{ time, midi, clarity? }] in order; notes: candidates
 * { midi, tuning? | cents? }; before: the pitched reading just before, if
 * the singing ran straight on from another note (else null); judgeScoop:
 * whether a scoop can be told at all (not when the note was only found
 * by its written time).
 * Returns { target, offset (semitones), cents, grade, settle (seconds),
 * scoop, graded (the readings graded), start, end }.
 */
export function judgeSung({ heard, notes, hopSeconds = 0.01, before = null, judgeScoop = true }) {
  // Settling is judged on the sung pitch alone, so a chord changing under
  // a held note is not mistaken for unsettled singing. The reach is the
  // singer's own vibrato, so a straight tone has even a small scoop
  // trimmed and a wide vibrato is not mistaken for one.
  const sungLine = heard.map(h => h.midi);
  const lineMiddle = [...sungLine].sort((a, b) => a - b)[sungLine.length >> 1];
  const line = sungLine.map(m => m - 12 * Math.round((m - lineMiddle) / 12));
  const lookahead = Math.max(3, Math.round(SETTLE_LOOKAHEAD_SECONDS / hopSeconds));
  const medianOf = (values) => [...values].sort((a, b) => a - b)[values.length >> 1];
  // Where the note was held: the pitch it stayed at longest (see
  // heldPitch). A scoop in and a glide out are short and moving; on a
  // short note either can fill half of it, so neither its second half
  // nor its end is a safe reference.
  const { level: settledLevel, swing } = heldPitch(line, hopSeconds);
  const reach = Math.min(SETTLED_SEMITONES, Math.max(MIN_SETTLED_SEMITONES, 1.5 * swing));
  // Settled when the glide has stopped, or (on a note too short for that
  // to show) once it reaches where the note ends up. Whether a glide has
  // stopped can only be told on a straight tone: under a vibrato the
  // swing would hide a scoop, so there the note is settled once it comes
  // within the vibrato's reach of its level. A scoop may take most of a
  // short note, but the grade always keeps its last few frames.
  const straight = !vibratoPeriod(line, hopSeconds);
  const latest = Math.max(0, Math.min(Math.floor(line.length * 0.75), line.length - MIN_GRADED_FRAMES));
  let start = 0;
  while (start < latest) {
    const now = medianOf(line.slice(Math.max(0, start - 1), start + 2));
    // Holding still only counts near where the note was held: a burst of
    // pitch at the start of a word (a voiced consonant) can hold still for
    // a moment too, well away from the note itself
    const near = Math.abs(now - settledLevel) <= Math.max(2 * reach, NEAR_HELD_SEMITONES);
    const stopped = straight && near && Math.abs(now - medianOf(line.slice(start, start + lookahead))) <= reach;
    if (stopped || Math.abs(now - settledLevel) <= reach) break;
    start++;
  }
  // The mirror image at the end: stop where the voice starts to leave
  // the note for the next one (never cutting more than a third), and
  // always leave out the last few frames, the move itself
  const keep = Math.max(3, Math.ceil(((line.length - start) * 2) / 3));
  let end = line.length;
  while (end - start > keep) {
    if (Math.abs(medianOf(line.slice(Math.max(start, end - 2), end)) - settledLevel) <= reach) break;
    end--;
  }
  if (end === line.length && end - start > RELEASE_FRAMES + 3) end -= RELEASE_FRAMES;
  // The pitch is judged on clearly pitched readings whenever there are
  // enough: less clear ones (breathy tone, a noisy room or a laptop mic)
  // help find where the note is, but can be junk
  const between = heard.slice(start, end);
  const clear = between.filter(h => h.clarity === undefined || h.clarity >= CLEAR_CLARITY);
  const graded = clear.length >= Math.max(MIN_GRADED_FRAMES, between.length * CLEAR_SHARE) ? clear : between;

  // A scoop: the voice arrived from well below the pitch it settled on
  // and took a while to get there (or slid down from above). Reported
  // on its own; the pitch grade is of the note once it arrived. A glide
  // that simply carries on from the note before (legato, coming from
  // that note's side) is the normal way between notes: it only counts
  // when it is slow.
  const settle = heard[start].time - heard[0].time;
  let scoop = null;
  if (judgeScoop && settle >= SCOOP_SECONDS) {
    const level = centreOf(line.slice(start, end), hopSeconds);
    const arrival = Math.round((medianOf(line.slice(0, 3)) - level) * 100);
    const carriedOn = before !== null && Math.sign(foldOctave(before.midi - level)) === Math.sign(arrival);
    // A scoop glides; a burst at the start of a word (a voiced consonant)
    // jumps. The pitch reading smears a jump over a few frames, so the
    // speed is measured across two: faster than JUMP_IN_SEMITONES a frame
    // on the way in means the voice jumped, not scooped.
    const way = line.slice(0, start + 1);
    const glided = way.every((m, i) => i < 2 || Math.abs(m - way[i - 2]) <= 2 * JUMP_IN_SEMITONES);
    if (glided && Math.abs(arrival) >= SCOOP_CENTS && settle >= (carriedOn ? SLIDE_SECONDS : SCOOP_SECONDS)) {
      scoop = { direction: arrival < 0 ? 'up' : 'down', cents: Math.abs(arrival), seconds: settle };
    }
  }

  const offsetFrom = (note) => {
    // Each frame against the target in force at its moment, stray
    // octave readings brought next to the rest
    const raw = graded.map(h => h.midi - tunedAt(note, h.time));
    const centre = [...raw].sort((a, b) => a - b)[raw.length >> 1];
    return foldOctave(centreOf(raw.map(d => d - 12 * Math.round((d - centre) / 12)), hopSeconds));
  };
  let target = notes[0];
  let offset = offsetFrom(target);
  for (const note of notes.slice(1)) {
    const candidate = offsetFrom(note);
    if (Math.abs(candidate) < Math.abs(offset)) {
      target = note;
      offset = candidate;
    }
  }
  const cents = Math.round(offset * 100);
  return { target, offset, cents, grade: gradeOf(cents), settle, scoop, graded, start, end };
}

// What the note inspector shows for a graded note: the sung pitch from a
// little before the note to a little after ([time, midi | null, role] with
// role 0 = another note's or a rest's, 1 = lined up with this note, 2 = the
// part graded), the target over the note's span, and `listen`, the stretch
// of the recording to play back (first and last input sample of what was
// lined up with the note, or of its written time if nothing was)
const INSPECT_MARGIN = 0.3;
// How far a note's playback reaches for consonants either side of its singing
const CONSONANT_SECONDS = 0.25;
const round = (value, places) => Math.round(value * 10 ** places) / 10 ** places;
function inspection(slot, note, points, own, graded) {
  const mine = new Set(own);
  const used = new Set(graded);
  // Points run in time order: find the first one in view
  let low = 0;
  let high = points.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (points[mid].time < slot.start - INSPECT_MARGIN) low = mid + 1;
    else high = mid;
  }
  const trace = [];
  for (let i = low; i < points.length && points[i].time <= slot.end + INSPECT_MARGIN; i++) {
    const p = points[i];
    trace.push([round(p.time, 3), p.midi === null ? null : round(p.midi, 3), used.has(p) ? 2 : mine.has(p) ? 1 : 0]);
  }
  const targets = note.tuning && note.tuning.length
    ? note.tuning.map(t => ({ from: t.from, to: t.to, midi: note.midi + t.cents / 100 }))
    : [{ from: slot.start, to: slot.end, midi: note.midi + (note.cents || 0) / 100 }];
  // Play from where this note's singing starts (a scoop into it included)
  // to where its grading stops, leaving out the glide on to the next note.
  // Consonants have no pitch, so they are not part of the singing: the
  // unpitched sound just before it is added (the "Ch" of "Christ"), and
  // when the note ends before the next one starts, its release and the
  // unpitched sound after it (the "st"), up to CONSONANT_SECONDS each way.
  const heardFor = own.length ? own : points.filter(p => p.time >= slot.start && p.time < slot.end);
  let listen = null;
  if (heardFor.length && heardFor[0].sample !== undefined) {
    const at = (point) => {
      let lo = 0;
      let hi = points.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (points[mid].time < point.time) lo = mid + 1;
        else hi = mid;
      }
      while (lo < points.length && points[lo] !== point) lo++;
      return lo < points.length ? lo : -1;
    };
    const first = heardFor[0];
    const lastOwn = heardFor[heardFor.length - 1];
    let start = first;
    let last = graded.length ? graded[graded.length - 1] : lastOwn;
    let i = at(first);
    while (i > 0 && points[i - 1].midi === null && first.time - points[i - 1].time <= CONSONANT_SECONDS) start = points[--i];
    let j = at(lastOwn);
    if (j >= 0 && j + 1 < points.length && points[j + 1].midi === null) {
      last = lastOwn;
      while (j + 1 < points.length && points[j + 1].midi === null && points[j + 1].time - lastOwn.time <= CONSONANT_SECONDS) last = points[++j];
    }
    listen = [start.sample, last.sample];
  }
  return { start: slot.start, end: slot.end, midi: note.midi, targets, trace, listen };
}

/**
 * Grades every note each pass covered. Note-first: the singing is lined up
 * with the written notes (see align), so each note is graded on what was
 * actually sung for it, however early or late it came in. Within a note,
 * grading starts once the pitch has settled (a scoop into the note or a
 * quick correction costs nothing) and stops before the move to the next.
 *
 * Returns one result per graded (pass, slot), in pass then score order:
 * { pass, slot, ref, grade, cents, sung, target, entry, settle }, where
 * grade is 'good' (within IN_TUNE_CENTS), 'close' (within CLOSE_CENTS),
 * 'off' (further: a different note) or 'missed' (not sung); `sung` and
 * `target` are MIDI numbers at the middle of the note (a just target can
 * move during a note: each frame is judged against the target of its
 * moment); `entry` is how late (or, negative, early) the singing started
 * and `settle` how long the pitch took to settle, in seconds; `scoop` is
 * { direction: 'up' | 'down', cents, seconds } when the voice slid into
 * the note from well below (or above), else null; `detail` is what the
 * note inspector draws (see inspection).
 *
 * A note is graded once its pass has gone LATE_SLACK past it (so a late
 * entry is not mistaken for a missed note), or when the pass is over
 * (`final`, or a later pass has begun).
 */
export function gradeTake({ frames, positions, slots, sung, lagFrames, hopSeconds = 0.01, final = false }) {
  const { passes, lastPass } = pointsByPass(frames, positions, sung, lagFrames);
  const states = statesOf(slots);
  const results = [];
  passes.forEach((points, pass) => {
    if (points.length === 0) return;
    const assigned = align(points, states);
    const over = final || pass < lastPass;
    const first = points[0].time;
    const last = points[points.length - 1].time;

    // Points per note state, in time order
    const byState = new Map();
    assigned.forEach((s, f) => {
      if (!states[s].slot) return;
      if (!byState.has(s)) byState.set(s, []);
      byState.get(s).push(points[f]);
    });

    states.forEach((state, s) => {
      const { slot, index } = state;
      if (!slot || slot.end - slot.start < SHORTEST_GRADED) return;
      // Only notes the pass covered, and only once a late entry would show
      if (first > slot.start + 0.05) return;
      if (!(last >= slot.end + LATE_SLACK || (over && last >= slot.end - 0.05))) return;

      const base = { pass, slot: index };
      const middle = (slot.start + slot.end) / 2;
      let own = byState.get(s) || [];
      let heard = own.filter(p => p.midi !== null);
      let entry = heard.length ? heard[0].time - slot.start : null;
      if (heard.length * hopSeconds < MIN_SUNG_SECONDS) {
        // Next to nothing lined up with this note, yet if there was singing
        // through its written time it was sung, just not as written (say
        // the previous pitch again): grade what was sung then
        const during = points.filter(p => p.midi !== null && p.time >= slot.start && p.time < slot.end);
        if (during.length * hopSeconds < (slot.end - slot.start) * 0.5) {
          const note = slot.notes[0];
          results.push({
            ...base, ref: note.ref, grade: 'missed', cents: null, sung: null, target: tunedAt(note, middle), entry: null, settle: null, scoop: null,
            detail: inspection(slot, note, points, own, [])
          });
          return;
        }
        own = during;
        heard = during;
        entry = null;
      }

      // The pitched singing just before this note's, if it ran straight on
      // (legato): a glide carrying on from it is not a scoop unless slow
      const at = points.indexOf(heard[0]);
      let before = null;
      for (let i = at - 1; i >= 0 && points[at].time - points[i].time <= LEGATO_GAP; i--) {
        if (points[i].midi !== null) {
          before = points[i];
          break;
        }
      }
      const { target, offset, cents, grade, settle, scoop, graded } = judgeSung({
        heard, notes: slot.notes, hopSeconds, before, judgeScoop: entry !== null
      });
      const targetMidi = tunedAt(target, middle);
      results.push({
        ...base,
        ref: target.ref,
        grade,
        cents,
        sung: targetMidi + offset,
        target: targetMidi,
        entry,
        settle,
        scoop,
        detail: inspection(slot, target, points, own, graded)
      });
    });
  });
  results.sort((a, b) => a.pass - b.pass || slots[a.slot].start - slots[b.slot].start);
  return results;
}

/** Per pass: how many notes were graded and how they went. */
export function summarisePasses(results) {
  const passes = new Map();
  results.forEach((result) => {
    if (!passes.has(result.pass)) {
      passes.set(result.pass, { pass: result.pass, graded: 0, good: 0, close: 0, off: 0, missed: 0, scooped: 0, centsSum: 0, sizeSum: 0, centsCount: 0 });
    }
    const summary = passes.get(result.pass);
    summary.graded++;
    summary[result.grade]++;
    if (result.scoop) summary.scooped++;
    if (result.grade === 'good' || result.grade === 'close') {
      summary.centsSum += result.cents;
      summary.sizeSum += Math.abs(result.cents);
      summary.centsCount++;
    }
  });
  return [...passes.values()].map(({ centsSum, sizeSum, centsCount, ...summary }) => ({
    ...summary,
    // Over the notes sung roughly in tune: how far off they were on average
    // (finer than the colours), and the average signed deviation, so a
    // steady lean sharp or flat shows even when every note is "in tune"
    average: centsCount ? Math.round(sizeSum / centsCount) : null,
    lean: centsCount ? Math.round(centsSum / centsCount) : null
  }));
}

/** The most recent grade of each notehead (repeats and loop passes collapse). */
export function latestByNote(results) {
  const latest = new Map();
  results.forEach((result) => latest.set(result.ref, result));
  return [...latest.values()];
}
